import { useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import AutoGrowTextarea from '../../components/AutoGrowTextarea/AutoGrowTextarea';
import PageMeta from '../../components/SEO/PageMeta';
import {
  ARRAY_TOOL_CATEGORY_LABELS,
  ArrayToolCategory,
  ArrayToolSlug,
  getArrayToolPath,
  getArrayToolRoute,
  getArrayToolsByCategory,
  getArrayToolStructuredData,
} from '../../config/arrayTools';
import {
  arrayDifference,
  arrayIntersection,
  arrayToCsv,
  chunkArray,
  flattenArray,
  formatJson,
  parseArrayInput,
  sortArray,
  stableValueKey,
  uniqueArray,
} from '../../utils/developerToolTransforms';
import styles from '../StringToolsPage/StringToolsPage.module.css';

const SAMPLE_ARRAY = `[
  { "id": 1, "name": "Ada" },
  { "id": 2, "name": "Linus" },
  { "id": 1, "name": "Ada" },
  [3, 4]
]`;

const SAMPLE_COMPARE_ARRAY = `[
  { "id": 2, "name": "Linus" },
  4,
  5
]`;

const TOOL_CATEGORIES: ArrayToolCategory[] = ['inspect', 'transform', 'compare'];

export default function ArrayToolsPage() {
  const { toolSlug } = useParams<{ toolSlug: string }>();
  const route = getArrayToolRoute(toolSlug);
  const isMasterPage = !toolSlug;
  const [input, setInput] = useState(SAMPLE_ARRAY);
  const [compareInput, setCompareInput] = useState(SAMPLE_COMPARE_ARRAY);
  const [chunkSize, setChunkSize] = useState(2);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const parsed = useMemo(() => parseArrayInput(input), [input]);
  const compareParsed = useMemo(() => parseArrayInput(compareInput), [compareInput]);
  const values = useMemo(() => parsed.value ?? [], [parsed.value]);
  const compareValues = useMemo(() => compareParsed.value ?? [], [compareParsed.value]);

  const results = useMemo(
    () => ({
      length: {
        items: values.length,
        unique: uniqueArray(values).length,
        nestedArrays: values.filter(Array.isArray).length,
      },
      unique: uniqueArray(values),
      sorted: sortArray(values),
      reversed: [...values].reverse(),
      flattened: flattenArray(values),
      chunked: chunkArray(values, chunkSize),
      csv: arrayToCsv(values),
      difference: arrayDifference(values, compareValues),
      intersection: arrayIntersection(values, compareValues),
      comparison: getArrayComparisonSummary(values, compareValues),
    }),
    [chunkSize, compareValues, values],
  );

  if (toolSlug && !route) {
    return <Navigate to={getArrayToolPath('array-length')} replace />;
  }

  const pageTitle = route?.title ?? 'Free Online Array Tools';
  const pageDescription =
    route?.description ??
    'Free online array tools for JavaScript developers: count, dedupe, sort, reverse, flatten, chunk, convert to CSV, and compare arrays.';
  const path = route ? getArrayToolPath(route.slug) : '/array-tools';
  const activeSlug = route?.slug;
  const isDedicatedComparePage = !isMasterPage && isCompareSlug(activeSlug);

  async function handleCopy(slug: string, value: string) {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedSlug(slug);
      window.setTimeout(() => setCopiedSlug(null), 1600);
    } catch {
      setCopiedSlug(null);
    }
  }

  function formatPrimaryInput() {
    if (parsed.value) {
      setInput(formatJson(parsed.value));
    }
  }

  function formatCompareInput() {
    if (compareParsed.value) {
      setCompareInput(formatJson(compareParsed.value));
    }
  }

  return (
    <>
      <PageMeta
        title={pageTitle}
        description={pageDescription}
        path={path}
        structuredData={route ? getArrayToolStructuredData(route) : undefined}
      />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden> / </span>
        {route ? (
          <>
            <Link to="/array-tools">Array Tools</Link>
            <span aria-hidden> / </span>
            <span>{route.shortLabel}</span>
          </>
        ) : (
          <span>Array Tools</span>
        )}
      </nav>

      <section className={styles.hero}>
        <p className={styles.eyebrow}>Free online array tools</p>
        <h1>{pageTitle}</h1>
        <p>{pageDescription}</p>
      </section>

      <nav className={styles.toolNavGroups} aria-label="Array tool shortcuts">
        {TOOL_CATEGORIES.map((category) => (
          <div key={category} className={styles.toolNavGroup}>
            <span className={styles.toolNavLabel}>{ARRAY_TOOL_CATEGORY_LABELS[category]}</span>
            <div className={styles.toolNav}>
              {getArrayToolsByCategory(category).map((tool) => (
                <Link
                  key={tool.slug}
                  to={getArrayToolPath(tool.slug)}
                  className={`${styles.toolLink} ${
                    tool.slug === activeSlug ? styles.activeToolLink : ''
                  }`}
                >
                  {tool.shortLabel}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {isDedicatedComparePage ? (
        <section className={styles.compareWorkspace} aria-labelledby="array-input-heading">
          <div className={styles.compareInputGrid}>
            <ArrayInputPanel
              id="array-input"
              label="Enter array"
              input={input}
              rows={12}
              error={parsed.error}
              onChange={setInput}
              onFormat={formatPrimaryInput}
            />
            <ArrayInputPanel
              id="array-compare-input"
              label="Compare array"
              input={compareInput}
              rows={12}
              error={compareParsed.error}
              onChange={setCompareInput}
              onFormat={formatCompareInput}
            />
          </div>
          <div className={styles.resultSections}>
            <ArrayCompareResults
              isMasterPage={false}
              activeSlug={activeSlug}
              routeLabel={route?.label}
              compareError={compareParsed.error}
              difference={formatJson(results.difference)}
              intersection={formatJson(results.intersection)}
              comparison={results.comparison}
              copiedSlug={copiedSlug}
              onCopy={handleCopy}
            />
          </div>
        </section>
      ) : (
        <section
          className={`${styles.workspace} ${
            isMasterPage ? styles.masterWorkspace : styles.dedicatedWorkspace
          }`}
          aria-labelledby="array-input-heading"
        >
          <ArrayInputPanel
            id="array-input"
            label="Enter array"
            input={input}
            rows={isMasterPage ? 7 : 14}
            error={parsed.error}
            onChange={setInput}
            onFormat={formatPrimaryInput}
            helperText="Paste a JSON array, comma-separated list, or line-separated list."
          />

          <div className={styles.resultSections}>
            {(isMasterPage || activeSlug === 'array-length') && (
              <ResultSection title={isMasterPage ? 'Inspect' : 'Array length'}>
                <StatsCard
                  active={activeSlug === 'array-length'}
                  stats={[
                    ['Items', results.length.items],
                    ['Unique', results.length.unique],
                    ['Nested arrays', results.length.nestedArrays],
                  ]}
                />
              </ResultSection>
            )}

            {(isMasterPage || isTransformSlug(activeSlug)) && (
              <ResultSection title={isMasterPage ? 'Transform' : (route?.label ?? 'Transform')}>
                {(isMasterPage || activeSlug === 'remove-duplicates') && (
                  <ResultCard
                    slug="remove-duplicates"
                    activeSlug={activeSlug}
                    title="Remove duplicates"
                    value={formatJson(results.unique)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'sort-array') && (
                  <ResultCard
                    slug="sort-array"
                    activeSlug={activeSlug}
                    title="Sort array"
                    value={formatJson(results.sorted)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'reverse-array') && (
                  <ResultCard
                    slug="reverse-array"
                    activeSlug={activeSlug}
                    title="Reverse array"
                    value={formatJson(results.reversed)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'flatten-array') && (
                  <ResultCard
                    slug="flatten-array"
                    activeSlug={activeSlug}
                    title="Flatten array"
                    value={formatJson(results.flattened)}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'chunk-array') && (
                  <article
                    id="chunk-array"
                    className={`${styles.resultCard} ${
                      activeSlug === 'chunk-array' ? styles.activeResultCard : ''
                    }`}
                  >
                    <div className={styles.cardHeader}>
                      <h3>Chunk array</h3>
                      <button
                        type="button"
                        className={styles.copyButton}
                        onClick={() => handleCopy('chunk-array', formatJson(results.chunked))}
                      >
                        {copiedSlug === 'chunk-array' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <label className={styles.inlineOption}>
                      Chunk size
                      <input
                        type="number"
                        min="1"
                        value={chunkSize}
                        onChange={(event) => setChunkSize(Number(event.target.value) || 1)}
                      />
                    </label>
                    <output>{formatJson(results.chunked)}</output>
                  </article>
                )}
                {(isMasterPage || activeSlug === 'array-to-csv') && (
                  <ResultCard
                    slug="array-to-csv"
                    activeSlug={activeSlug}
                    title="Array to CSV"
                    value={results.csv}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
              </ResultSection>
            )}

            {(isMasterPage || isCompareSlug(activeSlug)) && (
              <>
                {isMasterPage && (
                  <div className={styles.compareInputGrid}>
                    <ArrayInputPanel
                      id="array-compare-input"
                      label="Compare array"
                      input={compareInput}
                      rows={5}
                      error={compareParsed.error}
                      onChange={setCompareInput}
                      onFormat={formatCompareInput}
                    />
                  </div>
                )}
                <ArrayCompareResults
                  isMasterPage={isMasterPage}
                  activeSlug={activeSlug}
                  routeLabel={route?.label}
                  compareError={compareParsed.error}
                  difference={formatJson(results.difference)}
                  intersection={formatJson(results.intersection)}
                  comparison={results.comparison}
                  copiedSlug={copiedSlug}
                  onCopy={handleCopy}
                />
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
}

function ArrayInputPanel({
  id,
  label,
  input,
  rows,
  error,
  helperText,
  autoGrow = true,
  onChange,
  onFormat,
}: {
  id: string;
  label: string;
  input: string;
  rows: number;
  error: string | null;
  helperText?: string;
  autoGrow?: boolean;
  onChange: (value: string) => void;
  onFormat: () => void;
}) {
  return (
    <div className={styles.inputPanel}>
      <div className={styles.inputHeader}>
        <span>{label}</span>
        <button
          type="button"
          className={styles.copyButton}
          onClick={onFormat}
          disabled={Boolean(error)}
        >
          Format
        </button>
      </div>
      <label htmlFor={id} className={styles.srOnly}>
        {label}
      </label>
      <AutoGrowTextarea
        id={id}
        value={input}
        onValueChange={onChange}
        autoGrow={autoGrow}
        rows={rows}
        placeholder='["apple", "banana", "apple"]'
      />
      {helperText && <p className={styles.helperText}>{helperText}</p>}
      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  );
}

function ArrayCompareResults({
  isMasterPage,
  activeSlug,
  routeLabel,
  compareError,
  difference,
  intersection,
  comparison,
  copiedSlug,
  onCopy,
}: {
  isMasterPage: boolean;
  activeSlug: ArrayToolSlug | undefined;
  routeLabel: string | undefined;
  compareError: string | null;
  difference: string;
  intersection: string;
  comparison: ArrayComparisonSummary;
  copiedSlug: string | null;
  onCopy: (slug: string, value: string) => void;
}) {
  return (
    <ResultSection title={isMasterPage ? 'Compare' : (routeLabel ?? 'Compare')}>
      {compareError && <p className={styles.errorText}>{compareError}</p>}
      <ArrayComparisonSummaryCard summary={comparison} />
      {(isMasterPage || activeSlug === 'array-difference') && (
        <ResultCard
          slug="array-difference"
          activeSlug={activeSlug}
          title="Array difference"
          value={difference}
          copiedSlug={copiedSlug}
          onCopy={onCopy}
        />
      )}
      {(isMasterPage || activeSlug === 'array-intersection') && (
        <ResultCard
          slug="array-intersection"
          activeSlug={activeSlug}
          title="Array intersection"
          value={intersection}
          copiedSlug={copiedSlug}
          onCopy={onCopy}
        />
      )}
    </ResultSection>
  );
}

interface ArrayComparisonSummary {
  removed: unknown[];
  added: unknown[];
  changed: Array<{ index: number; from: unknown; to: unknown }>;
}

function getArrayComparisonSummary(left: unknown[], right: unknown[]): ArrayComparisonSummary {
  const leftKeys = new Set(left.map(stableValueKey));
  const rightKeys = new Set(right.map(stableValueKey));
  const maxLength = Math.max(left.length, right.length);

  return {
    removed: left.filter((value) => !rightKeys.has(stableValueKey(value))),
    added: right.filter((value) => !leftKeys.has(stableValueKey(value))),
    changed: Array.from({ length: maxLength }, (_, index) => ({
      index,
      from: left[index],
      to: right[index],
    })).filter(
      (item) =>
        item.from !== undefined &&
        item.to !== undefined &&
        stableValueKey(item.from) !== stableValueKey(item.to),
    ),
  };
}

function ArrayComparisonSummaryCard({ summary }: { summary: ArrayComparisonSummary }) {
  return (
    <div className={styles.diffSummary}>
      <DiffGroup
        tone="removed"
        title="Removed"
        items={summary.removed.map((item) => formatJson(item))}
      />
      <DiffGroup
        tone="changed"
        title="Changed"
        items={summary.changed.map(
          (item) => `Index ${item.index + 1}: ${formatJson(item.from)} → ${formatJson(item.to)}`,
        )}
      />
      <DiffGroup tone="added" title="Added" items={summary.added.map((item) => formatJson(item))} />
    </div>
  );
}

function DiffGroup({
  tone,
  title,
  items,
}: {
  tone: 'removed' | 'changed' | 'added';
  title: string;
  items: string[];
}) {
  return (
    <section className={`${styles.diffGroup} ${styles[tone]}`}>
      <h3>
        {title} <span>{items.length}</span>
      </h3>
      {items.length > 0 ? (
        <ul>
          {items.map((item, index) => (
            <li key={`${item}-${index}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <p>No values</p>
      )}
    </section>
  );
}

function ResultSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.resultSection}>
      <h2 className={styles.resultSectionTitle}>{title}</h2>
      <div className={styles.resultGrid}>{children}</div>
    </section>
  );
}

function ResultCard({
  slug,
  activeSlug,
  title,
  value,
  copiedSlug,
  onCopy,
}: {
  slug: ArrayToolSlug;
  activeSlug: ArrayToolSlug | undefined;
  title: string;
  value: string;
  copiedSlug: string | null;
  onCopy: (slug: string, value: string) => void;
}) {
  return (
    <article
      id={slug}
      className={`${styles.resultCard} ${slug === activeSlug ? styles.activeResultCard : ''}`}
    >
      <div className={styles.cardHeader}>
        <h3>{title}</h3>
        <button
          type="button"
          className={styles.copyButton}
          onClick={() => onCopy(slug, value)}
          disabled={!value}
        >
          {copiedSlug === slug ? 'Copied' : 'Copy'}
        </button>
      </div>
      <output>{value || '[]'}</output>
    </article>
  );
}

function StatsCard({ active, stats }: { active: boolean; stats: Array<[string, number]> }) {
  return (
    <article
      className={`${styles.resultCard} ${styles.statsCard} ${active ? styles.activeResultCard : ''}`}
    >
      <div className={styles.cardHeader}>
        <h2>Array stats</h2>
      </div>
      <dl className={styles.statsGrid}>
        {stats.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

function isTransformSlug(slug: ArrayToolSlug | undefined): boolean {
  return (
    slug === 'remove-duplicates' ||
    slug === 'sort-array' ||
    slug === 'reverse-array' ||
    slug === 'flatten-array' ||
    slug === 'chunk-array' ||
    slug === 'array-to-csv'
  );
}

function isCompareSlug(slug: ArrayToolSlug | undefined): boolean {
  return slug === 'array-difference' || slug === 'array-intersection';
}
